import { useEffect, useState } from 'react';
import { api, date, dateTime } from '../api.js';
import { useAuth } from '../App.jsx';
import PasswordInput from '../components/PasswordInput.jsx';

/**
 * Служители, пароли и права.
 *
 * Потребител не се изтрива, а се деактивира — иначе документите му биха
 * останали без автор, а Приложение № 29 иска следата да е пълна.
 */
export default function Users() {
  const { user } = useAuth();
  const isAdmin = user.role === 'admin';

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [devicesFor, setDevicesFor] = useState(null);

  // Успехът и грешката се изключват взаимно — иначе на екрана остават
  // едновременно „не е вярна" отпреди малко и „сменена е" отсега.
  const showNotice = (msg) => { setError(null); setNotice(msg); };
  const showError = (msg) => { setNotice(null); setError(msg); };

  async function load() {
    if (!isAdmin) return null;
    const rows = await api.get('/api/users');
    setUsers(rows);
    return rows;
  }

  useEffect(() => {
    api.get('/api/roles').then(setRoles).catch(() => {});
    load().catch((e) => showError(e.message));
  }, []);

  if (!isAdmin) {
    return (
      <>
        <div className="page-head"><h1>Моят достъп</h1></div>
        <SelfCard user={user} roles={roles} onNotice={showNotice} onError={showError} />
        <div className="card">
          <div className="card-head">Моите устройства</div>
          <div className="card-body"><DevicesPanel userId={user.id} isSelf onError={showError} /></div>
        </div>
        {notice && <div className="notice">{notice}</div>}
        {error && <div className="error">{error}</div>}
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <h1>Служители и достъп</h1>
        <div className="spacer" />
        <button className="primary" onClick={() => setAdding(true)}>+ Нов служител</button>
      </div>

      {error && <div className="error">{error}</div>}
      {notice && <div className="notice">{notice}</div>}

      {adding && (
        <NewUserCard
          roles={roles}
          onCancel={() => setAdding(false)}
          onDone={async (msg) => { setAdding(false); showNotice(msg); await load(); }}
          onError={showError}
        />
      )}

      <div className="card">
        <div className="card-head">Служители</div>
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr>
                <th>Име</th><th>Потребител</th><th>Код</th><th>Роля</th>
                <th>Парола</th><th>Последен вход</th>
                <th className="num">Документи</th><th>Състояние</th><th />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.full_name}
                    {Number(u.is_service) ? (
                      <span className="badge muted" style={{ marginLeft: 6 }}
                        title="Връзка между програми, не човек — не влиза през екрана">служебен</span>
                    ) : null}
                  </td>
                  <td className="muted">{u.username}</td>
                  <td className="muted">{u.operator_code}</td>
                  <td>{roles.find((r) => r.role === u.role)?.name ?? u.role}</td>
                  <td>
                    {!Number(u.has_password)
                      ? <span className="badge danger">няма</span>
                      : Number(u.must_change_password)
                        ? <span className="badge warn" title="Дадена от администратор — сменя се при първото влизане">временна</span>
                        : <span className="badge ok">има</span>}
                  </td>
                  <td className="muted">{u.last_login_at ? dateTime(u.last_login_at) : '—'}</td>
                  <td className="num muted">{u.document_count}</td>
                  <td>
                    {Number(u.is_active)
                      ? <span className="badge ok">активен</span>
                      : <span className="badge muted">спрян</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {!Number(u.is_service) && (
                      <button style={{ padding: '4px 12px', marginRight: 6 }}
                        onClick={() => setDevicesFor(u)}>Устройства</button>
                    )}
                    <button
                      onClick={() => setEditing(u)}
                      className={Number(u.has_password) ? '' : 'primary'}
                      style={{ padding: '4px 12px' }}
                    >
                      {Number(u.has_password) ? 'Промени' : 'Задай парола'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditUserModal
          target={editing}
          roles={roles}
          currentUserId={user.id}
          onClose={() => setEditing(null)}
          onDone={async (msg, keepOpen) => {
            showNotice(msg);
            const fresh = await load();
            if (keepOpen) setEditing(fresh?.find((u) => u.id === editing.id) ?? editing);
            else setEditing(null);
          }}
          onError={showError}
        />
      )}

      {devicesFor && (
        <div className="modal-backdrop">
          <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 760 }}>
            <div className="card-head">Устройства — {devicesFor.full_name}</div>
            <div className="card-body">
              <DevicesPanel userId={devicesFor.id} isSelf={devicesFor.id === user.id} onError={showError} />
            </div>
            <div className="modal-foot">
              <button onClick={() => setDevicesFor(null)}>Затвори</button>
            </div>
          </div>
        </div>
      )}

      <SelfCard user={user} roles={roles} onNotice={showNotice} onError={showError} />

      <div className="card">
        <div className="card-head">Моите устройства</div>
        <div className="card-body"><DevicesPanel userId={user.id} isSelf onError={showError} /></div>
      </div>

      <AccessSettingsCard onNotice={showNotice} onError={showError} />

      <div className="card">
        <div className="card-head">Какво може всяка роля</div>
        <table className="compact">
          <thead>
            <tr><th>Роля</th><th>Обхват</th></tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.role}>
                <td><strong>{r.name}</strong></td>
                <td className="muted">{r.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function NewUserCard({ roles, onCancel, onDone, onError }) {
  const [form, setForm] = useState({
    username: '', fullName: '', operatorCode: '', role: 'operator', password: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));

  const ready = form.username.length >= 3 && form.fullName.length >= 3
    && /^\d{4}$/.test(form.operatorCode)
    && (form.password === '' || form.password.length >= 6);

  async function submit() {
    setBusy(true);
    try {
      await api.post('/api/users', {
        ...form,
        password: form.password || undefined,
      });
      onDone(`${form.fullName} е добавен.`);
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">Нов служител</div>
      <div className="card-body">
        <div className="form-grid">
          <label className="field">
            Име и фамилия
            <input value={form.fullName} autoComplete="off"
              onChange={(e) => set({ fullName: e.target.value })} />
          </label>
          <label className="field">
            Потребителско име
            <input value={form.username} autoComplete="off"
              onChange={(e) => set({ username: e.target.value.toLowerCase() })} />
            <span className="hint">латиница, цифри, точка и тире</span>
          </label>
          <label className="field">
            Код на оператор
            <input value={form.operatorCode} maxLength={4} inputMode="numeric" autoComplete="off"
              onChange={(e) => set({ operatorCode: e.target.value.replace(/\D/g, '') })} />
            <span className="hint">4 цифри, влиза в УНП</span>
          </label>
          <label className="field">
            Роля
            <select value={form.role} onChange={(e) => set({ role: e.target.value })}>
              {roles.filter((r) => !r.service).map((r) => <option key={r.role} value={r.role}>{r.name}</option>)}
            </select>
          </label>
          <label className="field">
            Парола
            <PasswordInput value={form.password} onChange={(v) => set({ password: v })}
              autoComplete="new-password" />
            <span className="hint">временна — служителят си слага своя при първото влизане</span>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button onClick={onCancel}>Откажи</button>
          <button className="primary" onClick={submit} disabled={!ready || busy}>
            {busy ? 'Записване…' : 'Добави'}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Всичко за един служител на едно място: данни, роля, достъп и парола.
 *
 * Паролата стои тук нарочно. Когато беше отделна карта под таблицата, се
 * оказваше точно до „Моята парола" и двете се бъркаха — човек попълваше
 * своята, мислейки че сменя чуждата.
 */
function EditUserModal({ target, roles, currentUserId, onClose, onDone, onError }) {
  const [fullName, setFullName] = useState(target.full_name);
  const [role, setRole] = useState(target.role);
  const [isActive, setIsActive] = useState(!!Number(target.is_active));
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);

  const isSelf = target.id === currentUserId;
  const hasPassword = !!Number(target.has_password);
  const passwordReady = password.length >= 6 && password === repeat;

  const detailsChanged =
    fullName !== target.full_name ||
    role !== target.role ||
    isActive !== !!Number(target.is_active);

  async function saveDetails() {
    setBusy(true);
    try {
      await api.patch(`/api/users/${target.id}`, { fullName, role, isActive });
      onDone(`Данните на ${fullName} са променени.`, false);
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function savePassword() {
    setBusy(true);
    try {
      await api.put(`/api/users/${target.id}/password`, { password });
      setPassword(''); setRepeat('');
      onDone(`Паролата на ${target.full_name} е зададена.`, true);
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onCancel={onClose} title={`Служител — ${target.full_name}`}>
      <div className="card-body">
        <div className="form-grid">
          <label className="field">
            Име и фамилия
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="off" />
          </label>
          <label className="field">
            Роля
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              {roles.filter((r) => !r.service).map((r) => <option key={r.role} value={r.role}>{r.name}</option>)}
            </select>
          </label>
          <label className="field">
            Достъп
            <select value={isActive ? 'yes' : 'no'} disabled={isSelf}
              onChange={(e) => setIsActive(e.target.value === 'yes')}>
              <option value="yes">активен</option>
              <option value="no">спрян</option>
            </select>
            <span className="hint">
              {isSelf ? 'не можете да спрете себе си' : 'потребител не се изтрива, а се спира'}
            </span>
          </label>
          <div className="field">
            Потребител и код
            <div className="readonly-value muted">
              {target.username} · оператор {target.operator_code}
            </div>
            <span className="hint">не се променят — кодът влиза в УНП</span>
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          <button className="primary" onClick={saveDetails} disabled={busy || !detailsChanged}>
            {busy ? 'Записване…' : 'Запази данните'}
          </button>
        </div>

        <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid var(--border)' }} />

        <div className="card-head" style={{ padding: 0, border: 0, marginBottom: 10 }}>
          Парола на {target.full_name.split(' ')[0]}
        </div>
        <p className="hint" style={{ marginTop: 0 }}>
          {hasPassword
            ? 'Старата парола не е нужна. Вашата собствена не се променя оттук.'
            : 'Без парола този служител не може да влезе в системата.'}
        </p>
        {/* Браузърът предлага да запомни паролата с най-близкото текстово
            поле над нея — иначе хваща „Име и фамилия". Скритото поле му казва
            истинското потребителско име. */}
        <SkritPotrebitel username={target.username} />
        <div className="form-grid">
          <label className="field">
            Нова парола
            <PasswordInput value={password} onChange={setPassword} autoComplete="new-password"
              onEnter={() => passwordReady && savePassword()} />
            <span className="hint">поне 6 знака</span>
          </label>
          <label className="field">
            Повторете
            <PasswordInput value={repeat} onChange={setRepeat} autoComplete="new-password"
              onEnter={() => passwordReady && savePassword()} />
            {repeat && password !== repeat && (
              <span className="hint" style={{ color: 'var(--danger)' }}>не съвпадат</span>
            )}
          </label>
        </div>
        <div style={{ marginTop: 12 }}>
          <button className={hasPassword ? '' : 'primary'} onClick={savePassword}
            disabled={busy || !passwordReady}>
            {hasPassword ? 'Смени паролата' : 'Задай паролата'}
          </button>
        </div>
      </div>

      <div className="modal-foot">
        <button onClick={onClose}>Затвори</button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onCancel }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onCancel();
    }}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="card-head">{title}</div>
        {children}
      </div>
    </div>
  );
}

function SelfCard({ user, roles, onNotice, onError }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);

  const ready = current.length > 0 && next.length >= 6 && next === repeat;

  async function submit() {
    setBusy(true);
    try {
      await api.put('/api/auth/password', { currentPassword: current, newPassword: next });
      setCurrent(''); setNext(''); setRepeat('');
      onNotice('Паролата ви е сменена.');
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">Моята парола</div>
      <div className="card-body">
        <p className="hint" style={{ marginTop: 0 }}>
          {user.fullName} · {roles.find((r) => r.role === user.role)?.name ?? user.role} ·
          оператор {user.operatorCode}
        </p>
        <SkritPotrebitel username={user.username} />
        <div className="toolbar" style={{ marginBottom: 0 }}>
          <label className="field" style={{ minWidth: 190 }}>
            Текуща парола
            <PasswordInput value={current} onChange={setCurrent}
              autoComplete="current-password" onEnter={() => ready && submit()} />
          </label>
          <label className="field" style={{ minWidth: 190 }}>
            Нова парола
            <PasswordInput value={next} onChange={setNext}
              autoComplete="new-password" onEnter={() => ready && submit()} />
          </label>
          <label className="field" style={{ minWidth: 190 }}>
            Повторете
            <PasswordInput value={repeat} onChange={setRepeat}
              autoComplete="new-password" onEnter={() => ready && submit()} />
            {repeat && next !== repeat && (
              <span className="hint" style={{ color: 'var(--danger)' }}>не съвпадат</span>
            )}
          </label>
          <button className="primary" onClick={submit} disabled={!ready || busy}>
            {busy ? 'Записване…' : 'Смени паролата'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Устройствата на един потребител — регистрирани, откъде и кога ползвани.
 *
 * Извън офиса Тефтер пуска само регистрирани устройства (миграция 115).
 * Регистрира се с еднократен код (10 минути), въведен на самото
 * устройство, или с „Запомни това устройство" от компютър в офиса.
 */
function DevicesPanel({ userId, isSelf, onError }) {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState(null);
  const [name, setName] = useState('');
  const [vhodKod, setVhodKod] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/api/users/${userId}/devices`).then(setRows).catch((e) => onError(e.message));
  useEffect(() => {
    load();
    if (isSelf) api.get('/api/access/status').then(setStatus).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  async function pokana() {
    setBusy(true);
    try { setCode(await api.post(`/api/users/${userId}/device-invites`)); }
    catch (e) { onError(e.message); }
    finally { setBusy(false); }
  }

  async function zapomni() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post('/api/access/remember-this-device', { name: name.trim() });
      setName('');
      await load();
      api.get('/api/access/status').then(setStatus).catch(() => {});
    } catch (e) { onError(e.message); }
    finally { setBusy(false); }
  }

  /* Отвън: кодът, генериран горе (или от администратора), регистрира това устройство. */
  async function regsKod() {
    setBusy(true);
    try {
      await api.post('/api/access/register-device', { code: vhodKod.trim(), name: name.trim() });
      setVhodKod('');
      setName('');
      setCode(null);
      await load();
      api.get('/api/access/status').then(setStatus).catch(() => {});
    } catch (e) { onError(e.message); }
    finally { setBusy(false); }
  }

  async function spri(d) {
    if (!window.confirm(`Спиране на „${d.name}"? Оттам вече няма да може да се влиза отвън.`)) return;
    try { await api.del(`/api/devices/${d.id}`); await load(); } catch (e) { onError(e.message); }
  }

  return (
    <>
      {isSelf && status && (
        <p className="hint" style={{ marginTop: 0 }}>
          Това устройство: адрес {status.ip}
          {status.ipAllowed ? ' — разрешен (офис)' : ''}
          {status.device ? ` · регистрирано като „${status.device.name}"` : ' · не е регистрирано'}
        </p>
      )}

      <div className="table-wrap">
        <table className="compact">
          <thead>
            <tr><th>Устройство</th><th>Регистрирано</th><th>Последно ползвано</th><th>Състояние</th><th /></tr>
          </thead>
          <tbody>
            {(rows ?? []).map((d) => (
              <tr key={d.id} className={d.revoked_at ? 'muted' : undefined}>
                <td>
                  <strong>{d.name}</strong>
                  {d.isThis && <span className="badge ok" style={{ marginLeft: 6 }}>това</span>}
                  <div className="muted" style={{ fontSize: 11 }}>{(d.user_agent ?? '').slice(0, 70)}</div>
                </td>
                <td className="muted">{dateTime(d.created_at)} · {d.created_ip}</td>
                <td className="muted">{d.last_used_at ? `${dateTime(d.last_used_at)} · ${d.last_ip ?? ''}` : '—'}</td>
                <td>
                  {d.revoked_at
                    ? <span className="badge muted" title={d.revoked_by_name ?? ''}>спряно {date(d.revoked_at)}</span>
                    : <span className="badge ok">активно</span>}
                </td>
                <td>{!d.revoked_at && <button className="link" style={{ color: 'var(--danger)' }} onClick={() => spri(d)}>спри</button>}</td>
              </tr>
            ))}
            {rows && rows.length === 0 && <tr><td colSpan={5} className="empty">Няма регистрирани устройства</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="toolbar" style={{ marginTop: 12, marginBottom: 0 }}>
        <button onClick={pokana} disabled={busy}>Код за ново устройство</button>
        {code && (
          <span>
            Код: <strong style={{ fontSize: 20, letterSpacing: '0.2em' }}>{code.code}</strong>
            <span className="muted"> · въведи го на устройството до {code.minutes} минути</span>
          </span>
        )}
        {isSelf && !status?.ipAllowed && !status?.device && (
          <>
            <div className="spacer" />
            <input value={vhodKod} onChange={(e) => setVhodKod(e.target.value.toUpperCase())}
              placeholder="код" autoComplete="one-time-code" style={{ width: 110, letterSpacing: '0.15em' }} />
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="име, напр. iPhone Калин"
              autoComplete="off" style={{ minWidth: 160 }} />
            <button className="primary" disabled={busy || vhodKod.trim().length < 4 || !name.trim()}
              onClick={regsKod}>Регистрирай това устройство</button>
          </>
        )}
        {isSelf && status?.ipAllowed && !status?.device && (
          <>
            <div className="spacer" />
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="име, напр. iPhone Калин"
              autoComplete="off" style={{ minWidth: 180 }} />
            <button className="primary" onClick={zapomni} disabled={busy || !name.trim()}>
              Запомни това устройство
            </button>
          </>
        )}
      </div>
    </>
  );
}

/**
 * Достъпът отвън — режим и разрешени адреси. Само за администратор.
 * „Само отбелязва" пуска всички, но пише в дневника кой би бил спрян —
 * така се проверява, преди да се включи истинската забрана.
 */
function AccessSettingsCard({ onNotice, onError }) {
  const [s, setS] = useState(null);
  const [mode, setMode] = useState('report');
  const [ips, setIps] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/api/access/settings').then((r) => {
    setS(r); setMode(r.mode); setIps(r.allowedIps.join('\n'));
  }).catch((e) => onError(e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  async function save() {
    setBusy(true);
    try {
      await api.put('/api/access/settings', {
        mode, allowedIps: ips.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean),
      });
      onNotice('Настройката на достъпа е записана.');
      await load();
    } catch (e) { onError(e.message); }
    finally { setBusy(false); }
  }

  if (!s) return null;
  const MODES = [
    ['report', 'Само отбелязва', 'пуска всички, но пише в дневника кой би бил спрян'],
    ['enforce', 'Спира', 'отвън — само регистрирани устройства'],
    ['off', 'Изключено', 'без проверка'],
  ];
  return (
    <div className="card">
      <div className="card-head">Достъп отвън</div>
      <div className="card-body">
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div className="field">Режим</div>
            {MODES.map(([k, l, h]) => (
              <label key={k} className="check" style={{ display: 'flex', marginTop: 6 }}>
                <input type="radio" name="access-mode" checked={mode === k} onChange={() => setMode(k)} />
                <span><strong>{l}</strong> <span className="muted">— {h}</span></span>
              </label>
            ))}
          </div>
          <label className="field" style={{ minWidth: 220 }}>
            Разрешени адреси (по един на ред)
            <textarea rows={6} value={ips} onChange={(e) => setIps(e.target.value)} />
            <span className="muted">вашият адрес сега: {s.yourIp}</span>
          </label>
        </div>
        <div className="toolbar" style={{ marginTop: 12, marginBottom: 0 }}>
          <button className="primary" onClick={save} disabled={busy}>Запиши</button>
        </div>

        <div className="field" style={{ marginTop: 16 }}>Би били спрени (последните 7 дни)</div>
        <div className="table-wrap">
          <table className="compact">
            <thead><tr><th>Последно</th><th>Какво</th><th className="num">Пъти</th></tr></thead>
            <tbody>
              {s.recentWouldBlock.map((r) => (
                <tr key={r.note}><td className="muted">{dateTime(r.last_at)}</td><td>{r.note}</td><td className="num">{r.n}</td></tr>
              ))}
              {s.recentWouldBlock.length === 0 && <tr><td colSpan={3} className="empty">Никой</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * Потребителското име за мениджъра на пароли на браузъра — невидимо.
 * Не `display: none`: такова поле браузърът пренебрегва.
 */
function SkritPotrebitel({ username }) {
  return (
    <input type="text" name="username" autoComplete="username" value={username ?? ''} readOnly
      tabIndex={-1} aria-hidden="true"
      style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none', left: -9999 }} />
  );
}
